import React from "react";
import ReusableTable from "../../../../../../global components/Table/Table";
import NoData from "../../../../../../global components/Table/NoData/NoData";
import { PieChart } from "../../../../../../global components/Icons/PieChart";
import { TableColumnsType } from "antd";
import { PaginatedFinancialReportType } from "../../../../../../libs/Utils/Types/Reports";
import dayjs from "dayjs";
import Unauthorised from "../../../../../../global components/Unauthorised";
import { ArrowDownIcon } from "../../../../../../global components/Icons/ArrowDownIcon";
type ColumnsType = {
  key: string | number;
  date: string;
  creditUsed: string;
  spends: string;
  channelBreakDown: string;
  children?: ColumnsType[];
  isChild?: boolean;
};
type PropType = {
  isLoading: boolean;
  dataSource: PaginatedFinancialReportType["data"];
  unauthorized: boolean | { title: string; message: string };
};
function FinancialReportTable({
  dataSource,
  isLoading,
  unauthorized,
}: PropType) {
  const [expandedKeys, setExpandedKeys] = React.useState<React.Key[]>([]);

  const columns: TableColumnsType<ColumnsType> = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (_, record) =>
        record.date ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between", // space between date and icon
            }}
          >
            <span>{record.date}</span>

            {record.children?.length ? (
              <span
                onClick={(e) => {
                  // prevent row click from triggering
                  e.stopPropagation();
                  setExpandedKeys((prev) =>
                    prev.includes(record.key)
                      ? prev.filter((k) => k !== record.key)
                      : [...prev, record.key],
                  );
                }}
                style={{
                  borderRadius: "4px",
                  padding: "4px",
                  boxShadow: "0px 0px 0px 1px #0000001A",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transform: expandedKeys.includes(record.key)
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <ArrowDownIcon />
              </span>
            ) : null}
          </div>
        ) : null,
    },

    {
      title: "Channel Breakdown",
      dataIndex: "channelBreakDown",
      key: "channelBreakDown",
      render: (v, record) => (expandedKeys.includes(record.key) ? null : v),
    },
    {
      title: "Credit used",
      dataIndex: "creditUsed",
      key: "creditUsed",
      render: (v, record) => (expandedKeys.includes(record.key) ? null : v),
    },
    {
      title: "Spend ($)",
      dataIndex: "spends",
      key: "spends",
      render: (v, record) => (expandedKeys.includes(record.key) ? null : v),
    },
  ];
  const data: ColumnsType[] = dataSource.map((item, index) => {
    // Calculate total credits used across all channels
    const totalCreditsUsed = item.breakdown.reduce(
      (sum, b) => sum + b.totalCreditsUsed,
      0,
    );

    // Calculate total spend in USD across all channels
    const totalSpendsUsd = item.breakdown.reduce((sum, b) => {
      // remove $ if present, parse as float
      const val = parseFloat(b.usdEquivalent.replace(/[^0-9.-]+/g, ""));
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    // Create children rows for each channel (if you want expandable)
    const children = item.breakdown.map((b, idx) => ({
      key: `${index}-${idx}`,
      date: "", // optional: child rows usually don’t need a date
      creditUsed: b.totalCreditsUsed.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      spends: `$${Number(b.usdEquivalent).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      channelBreakDown: `${b.channel} (${b.percentageOfSpend}%)`,
    }));

    return {
      key: index,
      date: dayjs.utc(item.date).local().format("MMM DD, YYYY") || "N/A",
      creditUsed: totalCreditsUsed.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      spends: `$${totalSpendsUsd.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      channelBreakDown: item.breakdown
        .map((b) => `${b.channel} (${b.percentageOfSpend}%)`)
        .join(" ,"),
      children, // this makes the row expandable
    };
  });

  return (
    <ReusableTable
      columns={columns}
      dataSource={data}
      loading={isLoading}
      rowClassName={(record) => {
        // If parent row is expanded, highlight it
        if (expandedKeys.includes(record.key)) return "highlighted-row";

        // If this is a child row, highlight if its parent is expanded
        if (record.key.toString().includes("-")) {
          const parentKey = record.key.toString().split("-")[0];
          if (expandedKeys.includes(Number(parentKey)))
            return "highlighted-row";
        }

        return "";
      }}
      expandable={{
        expandIconColumnIndex: 0,
        expandedRowKeys: expandedKeys,
        onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
        rowExpandable: (record) => !!record.children?.length,

        expandIcon: ({ record }) => (record.children?.length ? "" : null),
      }}
      noDataComponent={
        unauthorized ? (
          typeof unauthorized === "boolean" ? (
            <Unauthorised />
          ) : (
            <Unauthorised error={unauthorized} />
          )
        ) : (
          <NoData
            Message="No financial report found"
            subMessage=""
            icon={<PieChart height={32} width={32} />}
          />
        )
      }
    />
  );
}

export default FinancialReportTable;
